import React from "react";
import { Tooltip, Typography } from "@mui/material";
import "../../styles/common.css";
import ArrowIcon from "../../assets/common/arrow-icon.svg";
import FeedBackIcon from "../../assets/common/feedBackIcon.svg";
import useGlobalContext from "../../context/useGlobalContext";

export type CardItem = {
  key: string | number;
  icon?: React.ReactNode;
  title: string;
  status?: string;
  statusColor?: string;
  paragraph?: { question: string; answer: string; reason: string }[];
  listA?: { title: string; items: (React.ReactNode | string)[] }; // second section
  listB?: { title: string; items: (React.ReactNode | string)[] }; // third section
  className?: string; // optional modifier on card
  onFeedbackClick?: (index?: number, answer?: string) => void;
};

export interface CommonCardsListProps {
  cards: CardItem[];
  containerClassName?: string;
}

const CommonCardsList: React.FC<CommonCardsListProps> = ({
  cards,
  containerClassName = "",
}) => {
  const { featureMap } = useGlobalContext();
  const userFeedback = featureMap?.idp_user_feedback === true;
  return (
    <div className={`common-cards-list ${containerClassName}`}>
      {cards.map((c) => (
        <div
          key={c.key}
          className={`common-info-card ${c.className ?? ""}`}
          role="region"
          aria-label={`card-${c.key}`}
        >
          {/* Header */}
          <div className="common-info-card__header">
            

            <div className="common-info-card__title-wrap">
              <span className="common-info-card__icon">{c.icon}</span>
              <Typography
              variant="h5"
               className="common-info-card__title">
                {c.title}
              </Typography>

              {c.status && (
                <Typography
                  variant="body2"
                  className="common-info-card__status"
                  style={{ color: c.statusColor ?? "#2BB673" }}
                >
                  {c.status}
                </Typography>
              )}
            </div>
          </div>

          {/* Body: three vertical sections */}
          <div className="common-info-card__body">
            {/* Section 1: paragraph */}
            {c.paragraph && (
              <div className="common-info-card__section">
                <div className="common-info-card__paragraph">
                  {Array.isArray(c.paragraph)
                    ? c.paragraph.map((qItem: any, idx: number) => {
                      const answer = qItem.answer?.toLowerCase();
                
                      const isPositive = qItem.reverse_score
                        ? answer === "no"
                        : answer === "yes";
                      return (
                        <div key={idx} className="question-item">
                          <p>
                            <span className="question-index">{idx+1}.</span>
                            <span className="question-text">
                              {qItem.question}
                              </span>
                            <span className="answer-feedback">
                            <span className="question-answer"
                              style={{
                                color: isPositive ? "#2BB673" : "#FF2E00",
                                fontWeight: "bold",
                              }}
                            >
                              {qItem.answer.charAt(0).toUpperCase() + qItem.answer.slice(1).toLowerCase()}
                            </span>
                            {userFeedback && 
                            <Tooltip
                              title="Give feedback on this tag"
                              placement="top"
                              arrow
                              classes={{
                                tooltip: "custom-tooltip",
                                arrow: "custom-tooltip-arrow",
                              }}
                            >
                              <img
                                src={FeedBackIcon}
                                alt="feedback"
                                className="feedback-icon"
                                onClick={() =>
                                  c.onFeedbackClick?.(
                                    idx,
                                    qItem.answer.charAt(0).toUpperCase() +
                                      qItem.answer.slice(1).toLowerCase()
                                  )
                                }
                              />
                            </Tooltip>
                            }
                            </span>
                          </p>
                          <p className="question-reason">
                            <img src={ArrowIcon} alt="arrow-icon" className="question-icon" /> {qItem.reason}
                          </p>
                        </div>
                      );
                    })
                    : c.paragraph}
                </div>
              </div>
            )}

            {/* Section 2: title + list */}
            {c.listA && (
              <div className="common-info-card__section">
                <div className="common-info-card__section-title">
                  <span className="common-info-card__icon">{c.icon}</span>
                  {c.listA.title}
                </div>
                <ol className="common-info-card__section-list">
                  {c.listA.items.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Section 3: title + list */}
            {c.listB && (
              <div className="common-info-card__section">
                <div className="common-info-card__section-title">
                  <span className="common-info-card__icon">{c.icon}</span>
                  {c.listB.title}
                </div>
                <ol className="common-info-card__section-list">
                  {c.listB.items.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CommonCardsList;
